import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactI18n from 'react-i18n';
import moment from 'moment-timezone';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import { faClone } from '@fortawesome/fontawesome-free-regular';
import {
  IconFontAwesome,
  TextBody,
  TextCaption,
  TextDense,
  TextH1,
  TextH2
} from '@directsoftware/ui-kit-web-admin';
import { formatCurrency } from '../../../../shared/helpers';
import { useDetectMobile } from '../../../../shared/hooks/useDetectMobile';
import { buildDetailsLink } from '../../listings/resources/shared-functions';
import { lifecyclePresentation } from '../../my-stay/requirement-presentation';

const t = (key, values) =>
  ReactI18n.getIntlMessage(`cx.receipt.redesign.${key}`, values);
const displayTime = (value, zone) => {
  if (!value) return '';
  const parsed = /[+-]\d{2}:?\d{2}$/.test(value)
    ? moment.parseZone(value)
    : moment.utc(value).tz(moment.tz.zone(zone) ? zone : 'UTC');
  return parsed.isValid() ? parsed.format('h:mm A') : '';
};

const ReceiptHeader = ({ receipt, stayState, brand }) => {
  const [copied, setCopied] = useState(false);
  const { isMobile } = useDetectMobile();
  const HeaderText = isMobile ? TextH2 : TextH1;
  const booking = receipt.booking || {};
  const location = receipt.location || {};
  const propertyName =
    (receipt.property || {}).name || (receipt.unit || {}).name || '';
  const locationLabel = [location.adr_city, location.adr_state]
    .filter(Boolean)
    .join(', ');
  let fallbackLifecycle = 'request_pending';
  if (booking.confirmed) fallbackLifecycle = 'confirmed';
  if (booking.cancelled) fallbackLifecycle = 'cancelled';
  const lifecycle = (stayState && stayState.lifecycle) || fallbackLifecycle;
  const presentation = lifecyclePresentation(lifecycle);
  const image = receipt.featured_image || {};
  const imageUrl =
    (image.image && image.image.small && image.image.small.url) || image.url;
  const closed = ['cancelled', 'declined', 'expired'].includes(lifecycle);
  const start = moment(booking.check_in, moment.ISO_8601, true);
  const end = moment(booking.check_out, moment.ISO_8601, true);
  const nights =
    start.isValid() && end.isValid() ? end.diff(start, 'days') : null;
  const guestCount = Number(booking.num_guests || 0);
  const guestName = (receipt.customer || {}).name || '—';
  const refund = stayState && stayState.refund;
  const refundText =
    refund && ['outstanding', 'partial', 'refunded'].includes(refund.status)
      ? ReactI18n.getIntlMessage(`cx.my_stay.refund.${refund.status}`, {
          amount: formatCurrency(
            Number(refund.amount_refunded || 0),
            brand.currency
          ),
          paid: formatCurrency(Number(refund.amount_paid || 0), brand.currency)
        })
      : null;
  const failed =
    !booking.confirmed &&
    !booking.cancelled &&
    brand.organization &&
    brand.organization.payment_processor === 2 &&
    receipt.payment_failure;
  const failedNote =
    failed &&
    Array.isArray(booking.notes) &&
    booking.notes.find(
      note => note.message && note.message.includes('Lynnbrook Card Error')
    );
  const failureDescription = `${
    failedNote
      ? failedNote.message.replace('Lynnbrook Card Error: ', '')
      : t('payment_failure')
  } ${t('payment_failure_contact')}`;
  const copy = () => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) return;
    navigator.clipboard
      .writeText(booking.booking_code)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setCopied(false));
  };
  const details = [
    {
      label: t('check_in'),
      value: start.isValid() ? start.format('ddd, MMM D, YYYY') : '—',
      sub: t('after', {
        time: displayTime(booking.check_in_time, location.timezone)
      }),
      icon: 'calendarAlt'
    },
    {
      label: t('check_out'),
      value: end.isValid() ? end.format('ddd, MMM D, YYYY') : '—',
      sub: t('by', {
        time: displayTime(booking.check_out_time, location.timezone)
      }),
      icon: 'calendarAlt'
    },
    {
      label: t('length'),
      value:
        nights == null
          ? '—'
          : t(nights === 1 ? 'night_stay_one' : 'night_stay', {
              count: nights
            }),
      sub:
        start.isValid() && end.isValid()
          ? `${start.format('MMM D')} – ${end.format('MMM D')}`
          : '',
      icon: 'bed'
    },
    {
      label: t('guests'),
      value: t(guestCount === 1 ? 'guest_one' : 'guest_many', {
        count: guestCount
      }),
      sub: t('reserved_for_name', { name: guestName }),
      icon: 'users'
    }
  ];
  return (
    <header className="receiptRedesign__heroWrap">
      <div
        className={`receiptRedesign__hero${
          closed ? ' receiptRedesign__hero--closed' : ''
        }`}
      >
        {imageUrl && (
          <img className="receiptRedesign__heroImage" src={imageUrl} alt="" />
        )}
        <div className="receiptRedesign__heroContent">
          <div className="receiptRedesign__heroTop">
            <span
              className={`receiptRedesign__heroStatus receiptRedesign__heroStatus--${
                presentation.color
              }`}
            >
              <span className="receiptRedesign__heroStatusIcon">
                <IconFontAwesome
                  name={
                    presentation.icon === 'checkCircle'
                      ? 'check'
                      : presentation.icon
                  }
                />
              </span>
              {presentation.pillKey && (
                <TextCaption
                  renderAs="span"
                  weight="semibold"
                  textTransform="uppercase"
                >
                  {ReactI18n.getIntlMessage(presentation.pillKey)}
                </TextCaption>
              )}
            </span>
            <div className="receiptRedesign__code">
              <div>
                <TextCaption textColor="white" weight="semibold" isFullWidth>
                  {t('booking_code').toUpperCase()}
                </TextCaption>
                <TextBody textColor="white" weight="bold" isFullWidth>
                  {booking.booking_code}
                </TextBody>
              </div>
              <button
                type="button"
                onClick={copy}
                aria-label={t('copy_code_with_value', {
                  code: booking.booking_code
                })}
              >
                <FontAwesomeIcon icon={faClone} aria-hidden="true" />
                <TextDense textColor="white" weight="semibold">
                  {copied ? t('copied') : t('copy')}
                </TextDense>
              </button>
              <span
                className="receiptRedesign__visuallyHidden"
                aria-live="polite"
              >
                {copied ? t('copied') : ''}
              </span>
            </div>
          </div>
          <div className="receiptRedesign__heroLocation">
            {propertyName && (
              <HeaderText
                renderAs="div"
                textColor="white"
                weight="bold"
                isFullWidth
              >
                {propertyName}
              </HeaderText>
            )}
            {locationLabel && (
              <span className="receiptRedesign__heroLocationItem">
                <span aria-hidden="true">
                  <IconFontAwesome name="mapMarkerAlt" iconStyle="regular" />
                </span>
                <TextBody renderAs="span" textColor="white">
                  {locationLabel}
                </TextBody>
              </span>
            )}
            {locationLabel && receipt.slug && (
              <span
                className="receiptRedesign__heroLocationBullet"
                aria-hidden="true"
              >
                ·
              </span>
            )}
            {receipt.slug && (
              <a href={buildDetailsLink(receipt)}>
                <span aria-hidden="true">
                  <IconFontAwesome name="externalLinkAlt" iconStyle="regular" />
                </span>
                <TextBody textColor="white">{t('view_listing')}</TextBody>
              </a>
            )}
          </div>
          <HeaderText renderAs="h1" textColor="white" weight="bold" isFullWidth>
            {ReactI18n.getIntlMessage(presentation.headlineKey)}
          </HeaderText>
          <TextBody renderAs="p" textColor="white" isFullWidth>
            {ReactI18n.getIntlMessage(presentation.descriptionKey)}
          </TextBody>
          {failed && (
            <div className="receiptRedesign__heroAlert" role="alert">
              <span
                className="receiptRedesign__alertIcon receiptRedesign__alertIcon--error"
                aria-hidden="true"
              >
                <IconFontAwesome name="exclamationCircle" iconStyle="regular" />
              </span>
              <div>
                <TextDense weight="semibold" textColor="dark-gray" isFullWidth>
                  {t('payment_failed')}
                </TextDense>
                <TextDense weight="regular" isFullWidth>
                  {failureDescription}
                </TextDense>
              </div>
            </div>
          )}
          {refundText && (
            <div className="receiptRedesign__heroAlert">
              <span
                className={`receiptRedesign__alertIcon receiptRedesign__alertIcon--${
                  refund.status === 'refunded' ? 'success' : 'neutral'
                }`}
                aria-hidden="true"
              >
                <IconFontAwesome
                  name={refund.status === 'refunded' ? 'checkCircle' : 'clock'}
                  iconStyle="regular"
                />
              </span>
              <div>
                <TextDense weight="semibold" textColor="dark-gray" isFullWidth>
                  {t(`refund_${refund.status}`)}
                </TextDense>
                <TextDense weight="regular" isFullWidth>
                  {refundText}
                </TextDense>
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className="receiptRedesign__trip"
        role="group"
        aria-label={t('stay_details')}
      >
        {details.map(detail => (
          <div className="receiptRedesign__tripCell" key={detail.label}>
            <span className="receiptRedesign__tripIcon">
              <IconFontAwesome name={detail.icon} />
            </span>
            <div>
              <TextCaption
                weight="semibold"
                textTransform="uppercase"
                isFullWidth
              >
                {detail.label}
              </TextCaption>
              <TextBody weight="bold" isFullWidth>
                {detail.value}
              </TextBody>
              <TextDense
                className="receiptRedesign__tripSub"
                title={detail.sub}
                truncate
                isFullWidth
              >
                {detail.sub}
              </TextDense>
            </div>
          </div>
        ))}
      </div>
    </header>
  );
};

ReceiptHeader.propTypes = {
  receipt: PropTypes.object.isRequired,
  stayState: PropTypes.object,
  brand: PropTypes.object.isRequired
};
ReceiptHeader.defaultProps = { stayState: null };
export default ReceiptHeader;
